import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Canonical from '../components/SEO/Canonical';
import GmbBadge from '../components/GmbBadge';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { countries } from '../lib/countries';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    countryCode: '+1',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Send contact details to backend API (which handles Firebase and Google Sheets sync)
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: `${formData.countryCode} ${formData.phone}`,
          service: formData.service,
          message: formData.message,
          sourcePage: 'Contact Page',
          formType: 'General Contact'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact details to backend');
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          countryCode: '+1',
          service: '',
          message: ''
        });
      }, 3000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAgencyDomain = typeof window !== 'undefined' && window.location.hostname.includes('xdmedia.agency');

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      detail: 'connect@xdmedia.in',
      action: 'mailto:connect@xdmedia.in'
    },
    ...(!isAgencyDomain ? [{
      icon: Phone,
      title: 'Call Us',
      detail: '+91 7901724043',
      action: 'tel:+917901724043'
    }] : []),
    ...(!isAgencyDomain ? [{
      icon: MapPin,
      title: 'Head Office , India',
      detail: 'SCO-40, HLP Galleria\nSAS Nagar Mohali, 160062\nChandigarh',
      action: '#'
    }] : []),
    {
      icon: MapPin,
      title: 'Branch Office , South Africa',
      detail: 'Sandton\nJohannesburg 2091\nSouth Africa',
      action: '#'
    }
  ];

  const services = [
    'SEO & Performance Optimization',
    'Digital Marketing & Analytics',
    'Website Design & Development',
    'Social Media Management',
    'Content & Video Marketing',
    'Branding & Creative Services'
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      <Helmet>
        <title>Contact Us | XD MEDIA - Digital Marketing & Tech Solutions</title>
        <meta
          name="description"
          content="Get in touch with XD MEDIA. Contact our team to discuss your SEO, web development, branding, or performance marketing goals today."
        />
        <meta property="og:title" content="Contact Us | XD MEDIA - Digital Marketing & Tech Solutions" />
        <meta property="og:description" content="Get in touch with XD MEDIA. Contact our team to discuss your SEO, web development, branding, or performance marketing goals today." />
        <meta property="twitter:title" content="Contact Us | XD MEDIA - Digital Marketing & Tech Solutions" />
        <meta property="twitter:description" content="Get in touch with XD MEDIA. Contact our team to discuss your SEO, web development, branding, or performance marketing goals today." />
      </Helmet>
      <Canonical path="/contact" />

      {/* Contact Form & Info */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#0a0a0a' }}>
        {/* Dynamic Background Glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400/5 rounded-full blur-[120px]"></div>
        </div>

        <div className="container-max relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16 text-center max-w-3xl mx-auto"
          >
            <div className="mb-4">
              <GmbBadge />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl leading-tight mb-4 tracking-tight" style={{ color: '#ffffff' }}>
              <span className="font-inter font-normal text-white" style={{ color: '#ffffff' }}>Let's Work </span>
              <span className="font-fraunces italic text-white" style={{ color: '#ffffff' }}>
                Together.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
              Ready to transform your digital presence? Contact us today and let's discuss how we can help you achieve your goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="group"
                >
                  <div className="rounded-2xl p-6 backdrop-blur-xl border transition-all duration-300" style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  }}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                      background: 'rgba(71, 191, 114, 0.2)',
                      color: '#47BF72'
                    }}>
                      <info.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{info.title}</h3>
                    <p className="text-gray-400 whitespace-pre-line">{info.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="rounded-3xl p-8 md:p-12 backdrop-blur-xl border" style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}>
                <h2 className="text-3xl font-bold text-white mb-2 font-inter">Send Us a Message</h2>
                <p className="text-gray-400 mb-8">Fill out the form below and we'll get back to you within 24 hours</p>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 rounded-lg flex items-center gap-3"
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}
                  >
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400">{error}</p>
                  </motion.div>
                )}

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{
                      background: 'rgba(71, 191, 114, 0.2)',
                      border: '2px solid #47BF72'
                    }}>
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                    <p className="text-gray-400">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                          placeholder="Your company name"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="sm:col-span-1 px-3 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                            style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              color: 'rgba(255, 255, 255, 0.7)'
                            }}
                          >
                            {countries.map((c, idx) => (
                              <option key={`${c.name}-${c.code}-${idx}`} value={c.code} style={{ background: '#1a1a2e', color: 'white' }}>
                                {c.name} ({c.code})
                              </option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="sm:col-span-2 px-4 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                            style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              color: 'white'
                            }}
                            placeholder="9999XXXXXX"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                        Service Interested In *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg transition-all duration-300 outline-none disabled:opacity-50"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }}
                      >
                        <option value="" style={{ background: '#1a1a2e' }}>Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service} style={{ background: '#1a1a2e' }}>{service}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg transition-all duration-300 resize-none outline-none disabled:opacity-50"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }}
                        placeholder="Tell us about your project and how we can help..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 rounded-lg font-semibold text-white transition-all duration-300 hover:scale-105 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      style={{
                        background: 'linear-gradient(135deg, #47BF72, #3aa85f)',
                        boxShadow: '0 10px 40px rgba(71, 191, 114, 0.3)'
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
